import React, { useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RootState } from 'MyTypes';
import {
    AppView,
    HydratedComparisonField,
    HydratedComparisonView,
    HydratedCurriculumTypeDynamicFilter,
    HydratedListView,
} from '../../../Common/types/AppConfigTypes';

import * as appConfigActions from '../../../AppConfig/appConfigActions';
import * as appConfigSelectors from '../../../AppConfig/appConfigSelectors';
import * as comparisonActions from '../../comparisonActions';
import * as comparisonSelectors from '../../comparisonSelectors';

import styles from './ComparisonView.module.scss';
import Loading from 'src/Common/components/Loading/Loading';

import { getNameValue, getComparisonItemFieldValue } from './helpers';
import { PayloadAction } from 'typesafe-actions';
import { ContentType } from '../../../Common/types/CatalogTypes';

type CurriculumType = {
    index: number;
    displayName: string;
    displayNamePlural?: string | undefined;
    slug: string;
    isAZ?: boolean | undefined;
    contentType: ContentType;
    filters: HydratedCurriculumTypeDynamicFilter[];
    listView: HydratedListView;
    comparisonView: HydratedComparisonView;
};

export type ComparisonItem = {
    uuid: string;
    [key: string]:
        | string
        | number
        | boolean
        | undefined
        | {
              [key: string]: string;
          };
};

type ComparisonHeadingProps = {
    displayName: string;
    setAppView: (payload: AppView) => PayloadAction<'SET_APP_VIEW', AppView>;
};

const mapStateToProps = (state: RootState) => ({
    activeCurriculumType: appConfigSelectors.activeCurriculumTypeSelector(
        state,
    ),
    comparisonItems: comparisonSelectors.comparisonSelectedItemsSelector(state),
    isLoading: comparisonSelectors.comparisonSelectedIsLoadingSelector(state),
    selectedUuids: comparisonSelectors.comparisonSelectedUuidsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            getComparisonSelected: comparisonActions.getComparisonSelected,
            removeComparisonViewItem:
                comparisonActions.removeComparisonViewItem,
            setAppView: appConfigActions.setAppView,
        },
        dispatch,
    );

type Props = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const ComparisonViewHeading = ({
    displayName,
    setAppView,
}: ComparisonHeadingProps) => {
    return (
        <div className={styles.comparisonHeaderContainer}>
            <div className={styles.comparisonHeader}>
                {displayName} Comparison
            </div>
            <button
                className={styles.comparisonButton}
                onClick={() => setAppView(AppView.List)}
            >
                {`< Back to ${displayName} List`}
            </button>
        </div>
    );
};

const FieldHeaderCell = ({
    comparisonField,
}: {
    comparisonField: HydratedComparisonField;
}) => (
    <div
        id={'comparisonview-field-item'}
        className={styles.fieldItem}
        key={comparisonField.field.uuid}
    >
        {comparisonField.displayName}
    </div>
);

const NameRow = ({
    comparisonView,
    comparisonItems,
    removeComparisonViewItem,
}: {
    comparisonView: HydratedComparisonView;
    comparisonItems: ComparisonItem[];
    removeComparisonViewItem: typeof comparisonActions.removeComparisonViewItem;
}) => {
    return (
        <div className={styles.fullRowContainer}>
            <div id={'comparisonview-field-item'} className={styles.fieldItem}>
                {comparisonView.name.field.displayName}
            </div>
            <div className={styles.valueRow}>
                {comparisonItems.map(comparisonItem => {
                    const name = getNameValue(comparisonView, comparisonItem);

                    return (
                        <div
                            className={styles.nameValueItem}
                            key={`name-${comparisonItem.uuid}`}
                        >
                            <div>{name}</div>
                            <button
                                id={'comparisonview-remove-item-button'}
                                className={styles.removeItemButton}
                                onClick={() =>
                                    removeComparisonViewItem(
                                        comparisonItem.uuid,
                                    )
                                }
                            >
                                <i className="material-icons">clear</i>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const DynamicRow = ({
    comparisonField,
    comparisonItems,
    activeCurriculumType,
}: {
    comparisonField: HydratedComparisonField;
    comparisonItems: ComparisonItem[];
    activeCurriculumType: CurriculumType;
}) => {
    return (
        <div className={styles.fullRowContainer}>
            <FieldHeaderCell comparisonField={comparisonField} />
            <div className={styles.valueRow}>
                {comparisonItems.map((comparisonItem: ComparisonItem) => (
                    <div
                        className={styles.valueItem}
                        key={`${comparisonItem.uuid}-${comparisonField.field.slug}`}
                    >
                        {getComparisonItemFieldValue(
                            comparisonField,
                            comparisonItem,
                            activeCurriculumType,
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const GoToPageRow = ({
    comparisonItems,
    curriculumType,
}: {
    comparisonItems: ComparisonItem[];
    curriculumType: CurriculumType;
}) => {
    return (
        <div className={styles.fullRowContainer}>
            <div className={styles.linkField} />
            <div className={styles.valueRow}>
                {comparisonItems.map(
                    (
                        comparisonItem: ComparisonItem & { _pagePath?: string },
                    ) => {
                        if (comparisonItem && comparisonItem._pagePath) {
                            return (
                                <div
                                    key={comparisonItem.uuid}
                                    className={styles.linkValueItem}
                                >
                                    <a
                                        key={`page-${comparisonItem.uuid}`}
                                        id={'comparisonview-page-link'}
                                        href={comparisonItem._pagePath}
                                        className={
                                            styles.curriculumListItemButton
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Go to {curriculumType.displayName}
                                    </a>
                                </div>
                            );
                        } else {
                            return (
                                <div
                                    key={comparisonItem.uuid}
                                    className={styles.linkValueItem}
                                />
                            );
                        }
                    },
                )}
            </div>
        </div>
    );
};

const CurriculumAddItem = ({
    setAppView,
    displayName,
}: ComparisonHeadingProps & { numberOfFields: number }) => {
    return (
        <div className={styles.addContainer}>
            <button
                id={'comparisonview-curriculum-add-item-button'}
                className={styles.curriculumAddItemButton}
                onClick={() => setAppView(AppView.List)}
            >
                <div className={styles.curriculumAddItemContainer}>
                    <div className={styles.curriculumAddLabel}>
                        {' '}
                        Add another {displayName} to compare
                    </div>
                    <div className={styles.curriculumAddIcon} />
                </div>
            </button>
        </div>
    );
};

const ComparisonView: React.FC<Props> = ({
    activeCurriculumType,
    getComparisonSelected,
    removeComparisonViewItem,
    isLoading,
    comparisonItems,
    selectedUuids,
    setAppView,
}) => {
    useEffect(() => {
        getComparisonSelected();
    }, [getComparisonSelected, activeCurriculumType]);

    if (!selectedUuids.length) {
        setAppView(AppView.List);
    }

    if (!activeCurriculumType || isLoading || !comparisonItems.length)
        return <Loading />;
    const { displayName, comparisonView } = activeCurriculumType;
    const { comparisonFields } = comparisonView;

    return (
        <main className={styles.comparisonView}>
            <ComparisonViewHeading
                displayName={displayName}
                setAppView={setAppView}
            />
            <section className={`${styles.comparisonBodyContainer}`}>
                <div className={styles.tableContainer}>
                    <NameRow
                        comparisonView={comparisonView}
                        comparisonItems={comparisonItems}
                        removeComparisonViewItem={removeComparisonViewItem}
                    />
                    {comparisonFields.map(comparisonField => {
                        return (
                            <DynamicRow
                                key={`row-${comparisonField.field.uuid}`}
                                comparisonField={comparisonField}
                                comparisonItems={comparisonItems}
                                activeCurriculumType={activeCurriculumType}
                            />
                        );
                    })}
                    <GoToPageRow
                        comparisonItems={comparisonItems}
                        curriculumType={activeCurriculumType}
                    />
                </div>
                {selectedUuids.length < 5 && (
                    <CurriculumAddItem
                        displayName={activeCurriculumType.displayName}
                        setAppView={setAppView}
                        numberOfFields={comparisonFields.length + 1}
                    />
                )}
            </section>
        </main>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ComparisonView);
